import { useEffect, useState } from 'react';
import { ActivityIndicator, Keyboard, ScrollView } from 'react-native';
import {
  Flex,
  PrimaryButton,
  StackScreen,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { useHandleBackPressed, useInstanceId } from 'shared/src/hooks';
import { useRoute } from 'shared/src/hooks/useRoute';
import { isIOS } from 'shared/src/utils';
import { closeAddressFlow } from 'mypage/src/utils/closeAddressFlow';
import type { AddressStackParamList } from 'mypage/src/pages/AddressList/AddressStackParamList';
import { updateAddressesCache } from 'mypage/src/pages/AddressList/AddressListScreen';
import { usePostAddress } from '../AddressList/queries/usePostAddress';
import { usePutAddress } from '../AddressList/queries/usePutAddress';
import { useChangeDefaultAddress } from '../AddressList/queries/useChangeDefaultAddress';
import { AddressTextField } from './components/AddressTextField';
import { formatPhone, validatePhoneNumber } from '../../utils/phone';

type EditorNavigation = {
  navigate: (
    route:
      | { name: 'Zipcode' }
      | {
          name: 'AddressList';
          params: { savedSno?: number; savedIsNew?: boolean };
          merge?: boolean;
        },
  ) => void;
  setParams: (params: Partial<AddressStackParamList['Editor']>) => void;
};

/**
 * 배송지 추가/수정 통합 화면 (iOS `AddressEditorVC` 대응).
 * - `addressSno` 유무로 추가/수정 분기.
 * - 우편번호 검색 결과는 Stack 내부 `navigation.navigate('Editor', { zipcodeResult })`로 받음.
 */
const AddressEditor = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { width } = useScreenSize();
  const insets = useSafeArea();
  const navigation = useNavigation<EditorNavigation>();
  const { params } = useRoute<AddressStackParamList, 'Editor'>();
  const instanceId = useInstanceId();

  const isEditMode = params?.addressSno !== undefined;

  const [name, setName] = useState(params?.name ?? '');
  const [zipcode, setZipcode] = useState(params?.zipcode ?? '');
  const [address, setAddress] = useState(params?.address ?? '');
  const [addressDetail, setAddressDetail] = useState(
    params?.addressDetail ?? '',
  );
  const [mobile, setMobile] = useState(formatPhone(params?.mobile ?? ''));
  const [shouldShowError, setShouldShowError] = useState(false);

  // 우편번호 검색 결과 수신 — Stack 내부 navigate로 전달된 params를 consume
  useEffect(() => {
    if (params?.zipcodeResult === undefined) return;
    setZipcode(params.zipcodeResult.zipcode);
    setAddress(params.zipcodeResult.address);
    navigation.setParams({ zipcodeResult: undefined });
  }, [navigation, params?.zipcodeResult]);

  // iOS `keyboardDismissButton` — 키보드 표시 중 키보드 위에 "완료" 버튼 노출
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, event =>
      setKeyboardHeight(event.endCoordinates.height),
    );
    const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const client = useQueryClient();
  const { mutateAsync: postAddress, isPending: isPosting } = usePostAddress();
  const { mutateAsync: putAddress, isPending: isPutting } = usePutAddress();
  const { mutateAsync: changeDefault } = useChangeDefaultAddress();
  const isSaving = isPosting || isPutting;

  // 모든 필드 + 전화번호 형식 (iOS `isEnabledSaveButton` 동일)
  const isValid =
    name.length > 0 &&
    zipcode.length > 0 &&
    address.length > 0 &&
    addressDetail.length > 0 &&
    validatePhoneNumber(mobile);

  // 수정 모드: 초기값 대비 변경이 있을 때만 저장 가능
  const isDirty =
    name !== (params?.name ?? '') ||
    zipcode !== (params?.zipcode ?? '') ||
    address !== (params?.address ?? '') ||
    addressDetail !== (params?.addressDetail ?? '') ||
    mobile !== formatPhone(params?.mobile ?? '');

  // 추가 모드는 필드 충족만, 수정 모드는 변경사항도 있어야 활성
  const canSave = isEditMode ? isValid && isDirty : isValid;

  const handlePressZipcode = () => {
    navigation.navigate({ name: 'Zipcode' });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setShouldShowError(false);

    try {
      const body = {
        name,
        zipcode,
        address,
        address_detail: addressDetail,
        mobile,
      };
      const { address: saved } = isEditMode
        ? await putAddress({ sno: params!.addressSno!, ...body })
        : await postAddress(body);

      // iOS: 추가 + shouldSetDefaultAddress → 기본 지정
      const isFirstAddressSetDefault =
        !isEditMode && params?.shouldSetDefaultAddress === true;
      if (isFirstAddressSetDefault) {
        await changeDefault({ default_address_sno: saved.sno });
      }

      // ENABLE-893: POST 응답은 changeDefault 반영 전 값이라 is_default_address=false.
      // 기본 지정까지 마친 경우 캐시에 true로 보정해야 재진입 시 기본배송지로 표시됨.
      updateAddressesCache(
        client,
        instanceId,
        isFirstAddressSetDefault
          ? { ...saved, is_default_address: true }
          : saved,
      );

      // iOS 패리티: 첫 배송지 추가(빈 목록 자동 진입, shouldSetDefaultAddress=true)
      // 케이스에서는 Editor + List 모두 빠져나가 마이페이지로 복귀해야 함
      // (iOS `AddressListVC.completeSetDefaultAddress()` 동일 동작).
      if (!isEditMode && params?.shouldSetDefaultAddress === true) {
        closeAddressFlow(instanceId);
        return;
      }

      // 그 외(일반 추가/수정): Editor만 pop하고 List로 복귀하며 선택 상태 반영
      navigation.navigate({
        name: 'AddressList',
        params: { savedSno: saved.sno, savedIsNew: !isEditMode },
        merge: true,
      });
    } catch {
      // iOS: 저장 실패 시 에러 표시
      setShouldShowError(true);
    }
  };

  return (
    <StackScreen
      title={isEditMode ? '배송지 변경' : '배송지 추가'}
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
    >
      {/* QA 자동화 화면 식별자(ENABLE-1174) — 헤더 글자가 목록과 동일('배송지 변경')해 화면 컨테이너로 구분 */}
      <Flex flex={1} testID={isEditMode ? '배송지수정' : '배송지추가'}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            width,
            marginHorizontal: 'auto',
            paddingHorizontal: 16,
            paddingTop: 16,
            paddingBottom: 40,
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <Flex gap={16}>
            <AddressTextField
              testID="받는분필드"
              value={name}
              placeholder="받는 분"
              onChangeText={text => {
                setName(() => {
                  if (text.length === 1) {
                    return text.trim();
                  }
                  return text;
                });
              }}
              errorMessage={
                shouldShowError && name.length === 0
                  ? '이름을 입력해주세요.'
                  : undefined
              }
            />
            <Flex.Row alignItems="center" gap={16}>
              <Flex flex={1}>
                <AddressTextField
                  testID="우편번호필드"
                  value={zipcode}
                  placeholder="우편번호"
                  onPress={handlePressZipcode}
                  errorMessage={
                    shouldShowError && zipcode.length === 0
                      ? '우편번호를 검색해주세요.'
                      : undefined
                  }
                />
              </Flex>
              <Flex width={108}>
                <PrimaryButton
                  testID="우편번호검색버튼"
                  variant="fill"
                  outline
                  color="gray"
                  size="large"
                  onPress={handlePressZipcode}
                >
                  우편번호 검색
                </PrimaryButton>
              </Flex>
            </Flex.Row>
            <AddressTextField
              testID="주소필드"
              value={address}
              placeholder="주소"
              onPress={handlePressZipcode}
              errorMessage={
                shouldShowError && address.length === 0
                  ? '주소를 검색해주세요.'
                  : undefined
              }
            />
            <AddressTextField
              testID="상세주소필드"
              value={addressDetail}
              placeholder="상세주소"
              onChangeText={setAddressDetail}
              errorMessage={
                shouldShowError && addressDetail.length === 0
                  ? '상세주소를 입력해주세요.'
                  : undefined
              }
            />
            <AddressTextField
              testID="연락처필드"
              value={mobile}
              placeholder="연락처"
              onChangeText={text => setMobile(formatPhone(text))}
              keyboardType="number-pad"
              errorMessage={
                shouldShowError && !validatePhoneNumber(mobile)
                  ? '연락처를 입력해주세요.'
                  : undefined
              }
            />
          </Flex>
        </ScrollView>
        <Flex
          borderTopWidth={1}
          borderColor="gray20"
          padding={{ top: 16, horizontal: 16, bottom: insets.bottom + 16 }}
          // 비활성 시 터치 이벤트 자체를 차단 (ui-kit PrimaryButton은 disabled여도 onPress 동작 — ENABLE-619)
          pointerEvents={canSave ? undefined : 'none'}
        >
          <PrimaryButton
            testID="배송지입력하기버튼"
            variant="fill"
            color="pink"
            size="xlarge"
            state={canSave ? undefined : 'disabled'}
            onPress={handleSave}
          >
            {isEditMode ? '배송지 입력하기' : '배송지 추가하기'}
          </PrimaryButton>
        </Flex>
        {isSaving && (
          <Flex
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            right={0}
            zIndex={1}
            backgroundColor="alpha_dark30"
            justifyContent="center"
            alignItems="center"
          >
            <ActivityIndicator color="white" />
          </Flex>
        )}
        {keyboardHeight > 0 && (
          <Flex
            position="absolute"
            left={0}
            right={0}
            // iOS는 화면이 안 줄어 키보드 높이만큼 띄우고,
            // Android(adjustResize)는 화면이 줄어 하단이 곧 키보드 위
            bottom={isIOS ? keyboardHeight : 0}
            zIndex={2}
          >
            <PrimaryButton
              variant="fill"
              color="pink"
              size="xlarge"
              onPress={Keyboard.dismiss}
            >
              완료
            </PrimaryButton>
          </Flex>
        )}
      </Flex>
    </StackScreen>
  );
};

export default AddressEditor;
